import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, Save, X } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportIncident = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [animals, setAnimals] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [formData, setFormData] = useState({
    residentName: '',
    tower: '',
    flatNo: '',
    species: '',
    issueType: 'Aggression',
    animalId: '',
    photoUrl: '',
    dateTime: new Date().toISOString().slice(0, 16),
    description: '',
  });

  useEffect(() => {
    fetchAnimals();
  }, []);

  const fetchAnimals = async () => {
    try {
      const response = await axios.get(`${API}/animals`);
      setAnimals(response.data);
    } catch (error) {
      console.error('Error fetching animals:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          photoUrl: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        animalId: formData.animalId || undefined,
      };
      await axios.post(`${API}/incidents`, submitData);
      navigate('/incidents');
    } catch (error) {
      console.error('Error reporting incident:', error);
      alert('Failed to report incident. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const issueTypes = [
    'Aggression',
    'Injury',
    'Feeding Issue',
    'Noise Complaint',
    'Stray Entry',
    'Medical Emergency',
    'Other'
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden">
            <div className="bg-gradient-to-br from-red-400 to-orange-500 p-8">
              <div className="flex items-center gap-4">
                <div className="bg-white p-3 rounded-xl shadow-lg">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <h1 className="text-3xl font-extrabold text-white font-heading" data-testid="report-incident-title">
                    Report Incident
                  </h1>
                  <p className="text-white/90 font-medium">Submit an animal-related complaint or concern</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Resident Name *</label>
                  <input
                    type="text"
                    name="residentName"
                    value={formData.residentName}
                    onChange={handleChange}
                    required
                    data-testid="input-resident-name"
                    placeholder="Your full name"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Tower *</label>
                  <select
                    name="tower"
                    value={formData.tower}
                    onChange={handleChange}
                    required
                    data-testid="input-tower"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                  >
                    <option value="">Select Tower</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Flat No *</label>
                  <input
                    type="text"
                    name="flatNo"
                    value={formData.flatNo}
                    onChange={handleChange}
                    required
                    data-testid="input-flat-no"
                    placeholder="e.g., 101"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Species</label>
                  <select
                    name="species"
                    value={formData.species}
                    onChange={handleChange}
                    data-testid="input-species"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                  >
                    <option value="">Select if known</option>
                    <option value="Dog">Dog</option>
                    <option value="Cat">Cat</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Issue Type *</label>
                  <select
                    name="issueType"
                    value={formData.issueType}
                    onChange={handleChange}
                    required
                    data-testid="input-issue-type"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                  >
                    {issueTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Animal ID (if known)</label>
                  <select
                    name="animalId"
                    value={formData.animalId}
                    onChange={handleChange}
                    data-testid="input-animal-id"
                    className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none font-medium"
                  >
                    <option value="">Select if known</option>
                    {animals.map(animal => (
                      <option key={animal.id} value={animal.id}>
                        {animal.name} ({animal.species} - {animal.area})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Date & Time *</label>
                <input
                  type="datetime-local"
                  name="dateTime"
                  value={formData.dateTime}
                  onChange={handleChange}
                  required
                  data-testid="input-datetime"
                  className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none"
                />
              </div>

              <div>
                <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Photo Attachment</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  data-testid="input-photo"
                  className="w-full h-12 px-4 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-hover"
                />
                {photoFile && (
                  <p className="text-sm text-green-600 mt-2 font-medium">✓ {photoFile.name} selected</p>
                )}
              </div>

              <div>
                <label className="text-sm font-bold text-stone-600 mb-1.5 ml-1 block">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  data-testid="input-description"
                  rows="6"
                  placeholder="Please provide detailed information about the incident..."
                  className="w-full px-4 py-3 rounded-xl border-2 border-orange-100 bg-white focus:border-primary focus:ring-4 focus:ring-orange-100 transition-all outline-none resize-none"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-button"
                  className="flex-1 bg-red-600 text-white shadow-lg shadow-red-500/30 hover:shadow-red-500/50 hover:-translate-y-0.5 transition-all duration-300 font-bold px-8 py-3 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-5 h-5 inline mr-2" />
                  {loading ? 'Submitting...' : 'Submit Report'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/incidents')}
                  data-testid="cancel-button"
                  className="bg-white text-primary border-2 border-orange-100 hover:border-primary hover:bg-orange-50 transition-all duration-300 font-bold px-8 py-3 rounded-full"
                >
                  <X className="w-5 h-5 inline mr-2" />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ReportIncident;